import type { Context } from '@sentry/react'
import { assertPlainReportingData, type ReportingData } from '@/core/reporting/payload'
import {
  ReportingEvent,
  ReportingTag,
  UsageEvent,
  type ReportingContext,
  type UsageDataByEvent,
} from '@/core/reporting/types'
import type { ToolTab } from '@/core/store/uiState'

export type SentryModule = typeof import('@sentry/react')

let sentry: SentryModule | null = null
const usedTools = new Set<ToolTab>()

export interface ReportingStatus {
  enabled: boolean
  lastEventId: string | null
}

export function installReportingClient(client: SentryModule): void {
  if (sentry) throw new Error('Error reporting client is already installed')
  sentry = client
}

export function setReportingTag(tag: ReportingTag, value: string | number | boolean): void {
  if (!Object.values(ReportingTag).includes(tag)) {
    throw new Error(`Unknown reporting tag: "${String(tag)}"`)
  }
  sentry?.setTag(tag, value)
}

export function getReportingStatus(): ReportingStatus {
  return {
    enabled: sentry?.isEnabled() === true,
    lastEventId: sentry?.lastEventId() || null,
  }
}

export function reportError(error: unknown, context: ReportingContext = {}): void {
  const activeSentry = sentry
  if (!activeSentry) return
  const normalized = error instanceof Error ? error : new Error('Unknown application error', { cause: error })
  activeSentry.withScope((scope) => {
    scope.setContext('app', context as Context)
    activeSentry.captureException(normalized)
  })
}

export function addBreadcrumb(event: ReportingEvent, data: ReportingData = {}): void {
  if (!Object.values(ReportingEvent).includes(event)) {
    throw new Error(`Unknown reporting event: "${String(event)}"`)
  }
  assertPlainReportingData(data)
  sentry?.addBreadcrumb({
    category: 'app',
    message: event,
    level: 'info',
    data: { ...data },
  })
}

export function trackUsage<Event extends UsageEvent>(event: Event, data: UsageDataByEvent[Event]): void {
  if (!Object.values(UsageEvent).includes(event)) {
    throw new Error(`Unknown usage event: "${String(event)}"`)
  }
  assertPlainReportingData(data as ReportingData)
  if (event === UsageEvent.ToolUsed) {
    const { toolId } = data as UsageDataByEvent[UsageEvent.ToolUsed]
    if (usedTools.has(toolId)) return
    usedTools.add(toolId)
  }
  sentry?.logger.info(event, { ...data })
}
